import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthContext";
import { api, API } from "../api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Wallet, Plus, RefreshCw, Search, Trash2, Edit3, X,
  CheckCircle2, AlertTriangle, Building2, Users, Receipt,
  Calendar, DollarSign, FileText, Upload, RotateCcw, Send,
} from "lucide-react";

const PAYMENT_METHODS = ["Bank Transfer", "Cash", "Cheque", "Online", "Card", "Other"];

// ---------- Helpers ----------
function fmtMoney(n) {
  return `RM ${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
}
async function fileToBase64(f) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result || "").split(",")[1] || "");
    r.onerror = rej;
    r.readAsDataURL(f);
  });
}

// ============================================================================
// MAIN PAGE
// ============================================================================
export default function Payouts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("candidates"); // candidates | clients
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [clientFilter, setClientFilter] = useState("");

  // Dialog states
  const [clientDlg, setClientDlg] = useState(null);    // { mode:"new"|"edit", row? }
  const [candDlg, setCandDlg] = useState(null);
  const [feeDlg, setFeeDlg] = useState(null);          // { candidate, fee? } add or edit
  const [payDlg, setPayDlg] = useState(null);          // fee object

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (clientFilter) params.client_id = clientFilter;
      const [{ data: s }, { data: cl }, { data: c }] = await Promise.all([
        api.get("/payouts/stats"),
        api.get("/payouts/clients"),
        api.get("/payouts/candidates", { params }),
      ]);
      setStats(s);
      setClients(cl || []);
      setCandidates(c || []);
    } catch (e) {
      toast.error("Failed to load payouts");
    } finally {
      setBusy(false);
    }
  }, [q, clientFilter]);

  useEffect(() => { load(); }, [load]);

  const feeTypes = stats?.fee_types || ["Levy", "Bank Guarantee", "FOMEMA", "Insurance SKHPPA", "EPF"];

  return (
    <Layout title="Payouts — Government Fees">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="w-10 h-10 rounded-lg bg-rose-600/20 border border-rose-600/40 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-rose-300" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">Payouts</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Track government / statutory fees we pay out for each candidate — Levy, Bank Guarantee, FOMEMA, Insurance SKHPPA, EPF.
            </p>
          </div>
          {isAdmin && (
            <div className="ml-auto">
              <Button size="sm" variant="outline"
                      onClick={async () => {
                        try {
                          const { data } = await api.post("/payouts/cron/reminders");
                          if (data.skipped_no_data) {
                            toast.message("Nothing overdue or due in the next 14 days. No reminders sent.");
                          } else if (data.sent > 0) {
                            toast.success(`WhatsApp digest sent to ${data.sent} recipient(s).`);
                          } else if (data.skipped > 0) {
                            toast.message(`Already sent today (${data.skipped} skipped). Add ?force=true via API to override.`);
                          } else {
                            toast.message("No eligible recipients (need wa_notify_enabled + phone).");
                          }
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Failed");
                        }
                      }}
                      className="h-9 border-emerald-700/50 text-emerald-300 hover:bg-emerald-500/10"
                      data-testid="payouts-send-reminders">
                <Send className="w-3.5 h-3.5 mr-1.5" /> Send WhatsApp digest
              </Button>
            </div>
          )}
        </div>

        {/* Stat cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <StatCard label="Candidates" value={stats.candidates} icon={Users} tone="blue"
                      testid="payouts-stat-cands" />
            <StatCard label="Clients" value={stats.clients} icon={Building2} tone="violet"
                      testid="payouts-stat-clients" />
            <StatCard label="Paid" sub={fmtMoney(stats.paid_total)} value={stats.paid_count}
                      icon={CheckCircle2} tone="green" testid="payouts-stat-paid" />
            <StatCard label="Pending" sub={fmtMoney(stats.pending_total)} value={stats.pending_count}
                      icon={Receipt} tone="amber" testid="payouts-stat-pending" />
            <StatCard label="Overdue" sub={fmtMoney(stats.overdue_total)} value={stats.overdue_count}
                      icon={AlertTriangle} tone="red" testid="payouts-stat-overdue" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-4">
          <TabPill active={tab === "candidates"} onClick={() => setTab("candidates")}
                   icon={Users} testid="payouts-tab-candidates">
            Candidates &amp; Fees
          </TabPill>
          <TabPill active={tab === "clients"} onClick={() => setTab("clients")}
                   icon={Building2} testid="payouts-tab-clients">
            Clients
          </TabPill>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={load} disabled={busy}
                  className="h-9 border-slate-700" data-testid="payouts-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {tab === "candidates" ? (
          <CandidatesView
            candidates={candidates}
            clients={clients}
            feeTypes={feeTypes}
            q={q} setQ={setQ}
            clientFilter={clientFilter} setClientFilter={setClientFilter}
            onNew={() => setCandDlg({ mode: "new" })}
            onEdit={(c) => setCandDlg({ mode: "edit", row: c })}
            onAddFee={(c, type) => setFeeDlg({ candidate: c, defaults: { fee_type: type } })}
            onEditFee={(c, f) => setFeeDlg({ candidate: c, fee: f })}
            onPayFee={(f) => setPayDlg(f)}
            onUnpayFee={async (f) => {
              if (!window.confirm(`Revert this ${f.fee_type} payment back to pending?`)) return;
              try {
                await api.post(`/payouts/fees/${f.id}/unpay`);
                toast.success("Fee reverted to pending");
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Failed");
              }
            }}
            onDeleteFee={async (f) => {
              if (!window.confirm(`Delete this ${f.fee_type} fee row? This cannot be undone.`)) return;
              try {
                await api.delete(`/payouts/fees/${f.id}`);
                toast.success("Fee deleted");
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Failed");
              }
            }}
            onDeleteCandidate={async (c) => {
              if (!window.confirm(`Delete candidate "${c.name}"?\n\nAll their payout fee records will also be removed. This cannot be undone.`)) return;
              try {
                await api.delete(`/payouts/candidates/${c.id}`);
                toast.success("Candidate deleted");
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Failed");
              }
            }}
            isAdmin={isAdmin}
          />
        ) : (
          <ClientsView
            clients={clients}
            onNew={() => setClientDlg({ mode: "new" })}
            onEdit={(c) => setClientDlg({ mode: "edit", row: c })}
            onDelete={async (c) => {
              if (!window.confirm(`Delete client "${c.name}"?\n\nThis cannot be undone.`)) return;
              try {
                await api.delete(`/payouts/clients/${c.id}`);
                toast.success("Client deleted");
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Failed");
              }
            }}
            isAdmin={isAdmin}
          />
        )}
      </div>

      {/* Dialogs */}
      <ClientDialog open={!!clientDlg} state={clientDlg} onClose={() => setClientDlg(null)}
                    onSaved={load} />
      <CandidateDialog open={!!candDlg} state={candDlg} clients={clients}
                       onClose={() => setCandDlg(null)} onSaved={load} />
      <FeeDialog open={!!feeDlg} state={feeDlg} feeTypes={feeTypes}
                 defaultAmounts={stats?.default_amounts}
                 onClose={() => setFeeDlg(null)} onSaved={load} />
      <PayDialog open={!!payDlg} fee={payDlg} onClose={() => setPayDlg(null)}
                 onSaved={load} />
    </Layout>
  );
}

// ============================================================================
// Stat card
// ============================================================================
function StatCard({ label, value, sub, icon: Icon, tone = "slate", testid }) {
  const toneCls = {
    blue:   "border-blue-500/40 text-blue-300 bg-blue-500/10",
    violet: "border-violet-500/40 text-violet-300 bg-violet-500/10",
    green:  "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    amber:  "border-amber-500/40 text-amber-300 bg-amber-500/10",
    red:    "border-red-500/40 text-red-300 bg-red-500/10",
    slate:  "border-slate-500/40 text-slate-300 bg-slate-500/10",
  }[tone];
  return (
    <div data-testid={testid}
      className={`rounded-lg border hairline surface-1 p-4 flex items-start gap-3`}>
      <div className={`w-9 h-9 rounded-md flex items-center justify-center border ${toneCls}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-xl font-semibold num tabular-nums mt-0.5">{value ?? 0}</div>
        {sub && <div className="text-[11px] text-slate-400 num">{sub}</div>}
      </div>
    </div>
  );
}

// ============================================================================
// Tab pill
// ============================================================================
function TabPill({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`h-9 px-3 rounded-md text-sm border transition flex items-center gap-1.5 ${
        active
          ? "bg-rose-600/15 border-rose-500/40 text-rose-200"
          : "border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700"
      }`}
    >
      <Icon className="w-4 h-4" />
      {children}
    </button>
  );
}

// ============================================================================
// Candidates view (grid: candidate x fee type)
// ============================================================================
function CandidatesView({
  candidates, clients, feeTypes, q, setQ, clientFilter, setClientFilter,
  onNew, onEdit, onAddFee, onEditFee, onPayFee, onUnpayFee, onDeleteFee,
  onDeleteCandidate, isAdmin,
}) {
  return (
    <>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by name, passport, or nationality…"
                 className="bg-[#0a0e1a] border-slate-800 pl-9 h-9"
                 data-testid="payouts-search-candidates" />
        </div>
        <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
                className="bg-[#0a0e1a] border border-slate-800 rounded h-9 px-2 text-sm"
                data-testid="payouts-filter-client">
          <option value="">All clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {(q || clientFilter) && (
          <Button size="sm" variant="ghost" className="h-9 text-xs text-slate-400"
                  onClick={() => { setQ(""); setClientFilter(""); }}>
            <X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        )}
        <Button onClick={onNew} className="h-9 bg-rose-600 hover:bg-rose-500"
                data-testid="payouts-new-candidate-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New candidate
        </Button>
      </div>

      {candidates.length === 0 ? (
        <div className="rounded-lg border hairline surface-1 text-center py-14 px-6">
          <Users className="w-10 h-10 text-slate-700 mx-auto mb-2" />
          <div className="text-slate-300 text-sm font-medium">
            {q || clientFilter ? "No candidates match your filters." : "No candidates yet."}
          </div>
          {!q && !clientFilter && (
            <div className="text-xs text-slate-500 mt-1">
              Click <span className="text-rose-300">New candidate</span> to add your first one.
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {candidates.map((c) => (
            <CandidateRow key={c.id} c={c} feeTypes={feeTypes}
                          onEdit={() => onEdit(c)}
                          onAddFee={(type) => onAddFee(c, type)}
                          onEditFee={(f) => onEditFee(c, f)}
                          onPayFee={onPayFee}
                          onUnpayFee={onUnpayFee}
                          onDeleteFee={onDeleteFee}
                          onDeleteCandidate={onDeleteCandidate}
                          isAdmin={isAdmin} />
          ))}
        </div>
      )}
    </>
  );
}

function CandidateRow({
  c, feeTypes, onEdit, onAddFee, onEditFee, onPayFee, onUnpayFee, onDeleteFee,
  onDeleteCandidate, isAdmin,
}) {
  // Group fees by type — show latest (most recent due_date) per type
  const feesByType = useMemo(() => {
    const map = {};
    (c.fees || []).forEach((f) => {
      const arr = map[f.fee_type] || [];
      arr.push(f);
      map[f.fee_type] = arr;
    });
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => (b.due_date || "").localeCompare(a.due_date || ""));
    });
    return map;
  }, [c.fees]);

  return (
    <div className="rounded-lg border hairline surface-1 p-4"
         data-testid={`payouts-candidate-row-${c.id}`}>
      {/* Top row — identity */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-md bg-rose-600/20 border border-rose-500/40 text-rose-300 flex items-center justify-center text-sm font-semibold shrink-0">
          {c.name?.charAt(0).toUpperCase() || "?"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold tracking-tight">{c.name}</div>
            {c.passport_no && (
              <span className="text-[11px] num text-slate-500">{c.passport_no}</span>
            )}
            {c.nationality && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-300">
                {c.nationality}
              </span>
            )}
          </div>
          {c.client_name && (
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
              <Building2 className="w-3 h-3" /> {c.client_name}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" onClick={onEdit}
                  className="h-8 w-8 text-slate-400 hover:text-slate-100"
                  data-testid={`payouts-edit-candidate-${c.id}`}>
            <Edit3 className="w-3.5 h-3.5" />
          </Button>
          {isAdmin && (
            <Button size="icon" variant="ghost" onClick={() => onDeleteCandidate(c)}
                    className="h-8 w-8 text-red-400 hover:text-red-300"
                    data-testid={`payouts-delete-candidate-${c.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Fee grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
        {feeTypes.map((type) => {
          const list = feesByType[type] || [];
          return (
            <FeeCell key={type} type={type} fees={list} candidateId={c.id}
                     onAdd={() => onAddFee(type)}
                     onEdit={(f) => onEditFee(f)}
                     onPay={(f) => onPayFee(f)}
                     onUnpay={(f) => onUnpayFee(f)}
                     onDelete={(f) => onDeleteFee(f)}
                     isAdmin={isAdmin} />
          );
        })}
      </div>
    </div>
  );
}

function FeeCell({ type, fees, candidateId, onAdd, onEdit, onPay, onUnpay, onDelete, isAdmin }) {
  // Show the latest row prominently, list older rows below collapsed
  const latest = fees[0];
  const older = fees.slice(1);
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-md border border-slate-800 bg-[#0a0e1a]/50 p-2.5"
         data-testid={`payouts-fee-cell-${candidateId}-${type.replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{type}</div>
        <button onClick={onAdd}
                className="text-[10px] text-rose-300 hover:text-rose-200 flex items-center gap-0.5"
                data-testid={`payouts-add-fee-${candidateId}-${type.replace(/\s+/g, "-")}`}>
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      {latest ? (
        <FeeRow f={latest} onEdit={() => onEdit(latest)}
                onPay={() => onPay(latest)} onUnpay={() => onUnpay(latest)}
                onDelete={() => onDelete(latest)} isAdmin={isAdmin} />
      ) : (
        <div className="text-[11px] text-slate-600 italic py-3 text-center">
          No record
        </div>
      )}
      {older.length > 0 && (
        <>
          <button onClick={() => setOpen((v) => !v)}
                  className="mt-1.5 text-[10px] text-slate-500 hover:text-slate-300">
            {open ? "Hide history" : `+${older.length} older`}
          </button>
          {open && (
            <div className="mt-1.5 space-y-1.5 border-t border-slate-800 pt-1.5">
              {older.map((f) => (
                <FeeRow key={f.id} f={f} compact
                        onEdit={() => onEdit(f)}
                        onPay={() => onPay(f)} onUnpay={() => onUnpay(f)}
                        onDelete={() => onDelete(f)} isAdmin={isAdmin} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FeeRow({ f, compact, onEdit, onPay, onUnpay, onDelete, isAdmin }) {
  const paid = f.status === "paid";
  const overdue = f.overdue;
  // Three pill states:
  //   green  -> paid (with paid_date)
  //   red    -> overdue OR pending (per user request, pending is red)
  //   default-red for any pending row, but emphasise overdue with darker red
  const pillCls = paid
    ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/40"
    : overdue
      ? "bg-red-600/30 text-red-100 border-red-500/60 ring-1 ring-red-500/40"
      : "bg-red-500/15 text-red-300 border-red-500/30";
  const label = paid
    ? `Paid ${fmtDate(f.paid_date)}`
    : overdue
      ? `OVERDUE · ${fmtDate(f.due_date)}`
      : `Due ${fmtDate(f.due_date)}`;
  return (
    <div className={`flex items-center justify-between gap-1 ${compact ? "" : "py-0.5"}`}>
      <div className="min-w-0 flex-1">
        <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border num ${pillCls}`}
             data-testid={`payouts-fee-pill-${f.id}`}>
          {paid && <CheckCircle2 className="w-3 h-3" />}
          {!paid && overdue && <AlertTriangle className="w-3 h-3" />}
          {label}
        </div>
        <div className="text-[11px] text-slate-400 num mt-0.5">{fmtMoney(paid ? f.paid_amount : f.amount)}</div>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        {!paid && (
          <button onClick={onPay} title="Mark paid"
                  className="p-1 rounded text-emerald-300 hover:bg-emerald-500/10"
                  data-testid={`payouts-pay-${f.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5" />
          </button>
        )}
        {paid && isAdmin && (
          <button onClick={onUnpay} title="Revert to pending (admin)"
                  className="p-1 rounded text-amber-300 hover:bg-amber-500/10"
                  data-testid={`payouts-unpay-${f.id}`}>
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
        {paid && f.receipt_r2_key && (
          <a href={`${API}/payouts/fees/${f.id}/receipt`}
             target="_blank" rel="noreferrer"
             title="View receipt"
             className="p-1 rounded text-slate-300 hover:bg-slate-700/40">
            <Receipt className="w-3.5 h-3.5" />
          </a>
        )}
        {!paid && (
          <button onClick={onEdit} title="Edit"
                  className="p-1 rounded text-slate-400 hover:bg-slate-700/40"
                  data-testid={`payouts-edit-fee-${f.id}`}>
            <Edit3 className="w-3.5 h-3.5" />
          </button>
        )}
        {isAdmin && (
          <button onClick={onDelete} title="Delete (admin)"
                  className="p-1 rounded text-red-400 hover:bg-red-500/10">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Clients view
// ============================================================================
function ClientsView({ clients, onNew, onEdit, onDelete, isAdmin }) {
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-400">
          A separate manual client list for Payouts — independent of your main Clients module.
        </div>
        <Button onClick={onNew} className="h-9 bg-rose-600 hover:bg-rose-500"
                data-testid="payouts-new-client-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New client
        </Button>
      </div>
      {clients.length === 0 ? (
        <div className="rounded-lg border hairline surface-1 text-center py-14 px-6">
          <Building2 className="w-10 h-10 text-slate-700 mx-auto mb-2" />
          <div className="text-slate-300 text-sm font-medium">No clients yet.</div>
          <div className="text-xs text-slate-500 mt-1">
            Click <span className="text-rose-300">New client</span> to add the first one.
          </div>
        </div>
      ) : (
        <div className="rounded-lg border hairline surface-1 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/40 text-[11px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left px-4 py-2">Client name</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Contact</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Phone</th>
                <th className="text-right px-4 py-2 w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-t border-slate-800/60 hover:bg-slate-800/20"
                    data-testid={`payouts-client-row-${c.id}`}>
                  <td className="px-4 py-2 font-medium">{c.name}</td>
                  <td className="px-4 py-2 text-slate-400 hidden md:table-cell">{c.contact_person || "—"}</td>
                  <td className="px-4 py-2 text-slate-400 num hidden md:table-cell">{c.phone || "—"}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="icon" variant="ghost" onClick={() => onEdit(c)}
                            className="h-7 w-7 text-slate-400 hover:text-slate-100"
                            data-testid={`payouts-edit-client-${c.id}`}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                    {isAdmin && (
                      <Button size="icon" variant="ghost" onClick={() => onDelete(c)}
                              className="h-7 w-7 text-red-400 hover:text-red-300"
                              data-testid={`payouts-delete-client-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Dialogs
// ============================================================================
function ClientDialog({ open, state, onClose, onSaved }) {
  const editing = state?.mode === "edit";
  const [form, setForm] = useState({ name: "", contact_person: "", phone: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing
      ? { name: state.row.name || "", contact_person: state.row.contact_person || "",
          phone: state.row.phone || "", notes: state.row.notes || "" }
      : { name: "", contact_person: "", phone: "", notes: "" }
    );
  }, [open, state, editing]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/payouts/clients/${state.row.id}`, form);
        toast.success("Client updated");
      } else {
        await api.post(`/payouts/clients`, form);
        toast.success("Client added");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md"
                     data-testid="payouts-client-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit client" : "New payout client"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="bg-[#0a0e1a] border-slate-800 mt-1"
                   placeholder="e.g. ABC Factory Sdn Bhd"
                   data-testid="payouts-client-name" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Contact person</Label>
            <Input value={form.contact_person}
                   onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                   className="bg-[#0a0e1a] border-slate-800 mt-1"
                   placeholder="HR Manager name" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Phone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   className="bg-[#0a0e1a] border-slate-800 mt-1"
                   placeholder="+60123456789" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                   className="bg-[#0a0e1a] border-slate-800 mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-500"
                  data-testid="payouts-client-save">
            {editing ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CandidateDialog({ open, state, clients, onClose, onSaved }) {
  const editing = state?.mode === "edit";
  const [form, setForm] = useState({ name: "", passport_no: "", nationality: "", client_id: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing
      ? { name: state.row.name || "", passport_no: state.row.passport_no || "",
          nationality: state.row.nationality || "", client_id: state.row.client_id || "",
          notes: state.row.notes || "" }
      : { name: "", passport_no: "", nationality: "", client_id: "", notes: "" }
    );
  }, [open, state, editing]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/payouts/candidates/${state.row.id}`, form);
        toast.success("Candidate updated");
      } else {
        await api.post(`/payouts/candidates`, form);
        toast.success("Candidate added");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md"
                     data-testid="payouts-candidate-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit candidate" : "New candidate"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">Full name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="bg-[#0a0e1a] border-slate-800 mt-1"
                   placeholder="e.g. Rohit Sharma"
                   data-testid="payouts-candidate-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-slate-400">Passport no.</Label>
              <Input value={form.passport_no}
                     onChange={(e) => setForm({ ...form, passport_no: e.target.value })}
                     className="bg-[#0a0e1a] border-slate-800 mt-1 num"
                     placeholder="A12345678" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Nationality</Label>
              <Input value={form.nationality}
                     onChange={(e) => setForm({ ...form, nationality: e.target.value })}
                     className="bg-[#0a0e1a] border-slate-800 mt-1"
                     placeholder="Indian, Bangladeshi…" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Client</Label>
            <select value={form.client_id}
                    onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                    className="w-full bg-[#0a0e1a] border border-slate-800 rounded h-9 px-2 text-sm mt-1"
                    data-testid="payouts-candidate-client">
              <option value="">— None —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {clients.length === 0 && (
              <div className="text-[10px] text-slate-500 mt-1">
                Tip: add clients in the “Clients” tab first.
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs text-slate-400">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                   className="bg-[#0a0e1a] border-slate-800 mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-500"
                  data-testid="payouts-candidate-save">
            {editing ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FeeDialog({ open, state, feeTypes, defaultAmounts, onClose, onSaved }) {
  const editing = !!state?.fee;
  const candidate = state?.candidate;
  // Default new fees to a due_date one year from today (annual cycle)
  const defaultDue = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  }, [open]);

  const [form, setForm] = useState({ fee_type: "Levy", amount: "", due_date: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const f = state.fee;
      setForm({
        fee_type: f.fee_type, amount: String(f.amount ?? ""),
        due_date: f.due_date, notes: f.notes || "",
      });
    } else {
      const initType = state?.defaults?.fee_type || feeTypes[0];
      setForm({
        fee_type: initType,
        amount: String(defaultAmounts?.[initType] ?? ""),
        due_date: defaultDue,
        notes: "",
      });
    }
  }, [open, state, editing, feeTypes, defaultAmounts, defaultDue]);

  // Auto-update amount when fee_type changes (only on create + when amount field
  // matches the previous type's default — don't stomp on a manually entered amount)
  const onTypeChange = (newType) => {
    setForm((prev) => {
      const prevDefault = defaultAmounts?.[prev.fee_type];
      const sameAsDefault = String(prevDefault) === prev.amount;
      return {
        ...prev,
        fee_type: newType,
        amount: editing || !sameAsDefault ? prev.amount : String(defaultAmounts?.[newType] ?? ""),
      };
    });
  };

  const submit = async () => {
    if (!form.fee_type || !feeTypes.includes(form.fee_type)) {
      toast.error("Pick a fee type"); return;
    }
    if (!form.due_date) { toast.error("Due date required"); return; }
    const amt = parseFloat(form.amount);
    if (Number.isNaN(amt) || amt < 0) { toast.error("Amount must be a number ≥ 0"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/payouts/fees/${state.fee.id}`, {
          fee_type: form.fee_type, amount: amt, due_date: form.due_date, notes: form.notes,
        });
        toast.success("Fee updated");
      } else {
        await api.post(`/payouts/candidates/${candidate.id}/fees`, {
          fee_type: form.fee_type, amount: amt, due_date: form.due_date, notes: form.notes,
        });
        toast.success("Fee added");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open || !candidate) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md"
                     data-testid="payouts-fee-dialog">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit fee" : `Add fee — ${candidate.name}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">Fee type *</Label>
            <select value={form.fee_type}
                    onChange={(e) => onTypeChange(e.target.value)}
                    className="w-full bg-[#0a0e1a] border border-slate-800 rounded h-9 px-2 text-sm mt-1"
                    data-testid="payouts-fee-type">
              {feeTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-slate-400">Amount (RM) *</Label>
              <Input type="number" step="0.01" value={form.amount}
                     onChange={(e) => setForm({ ...form, amount: e.target.value })}
                     className="bg-[#0a0e1a] border-slate-800 mt-1 num"
                     data-testid="payouts-fee-amount" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Due date *</Label>
              <Input type="date" value={form.due_date}
                     onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                     className="bg-[#0a0e1a] border-slate-800 mt-1"
                     data-testid="payouts-fee-due" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                   className="bg-[#0a0e1a] border-slate-800 mt-1" />
          </div>
          {!editing && (
            <div className="text-[11px] text-slate-500 leading-relaxed">
              Tip: duplicate {form.fee_type} fees for the same due date will be rejected automatically
              to prevent double-payment.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-500"
                  data-testid="payouts-fee-save">
            {editing ? "Save" : "Add fee"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayDialog({ open, fee, onClose, onSaved }) {
  const [form, setForm] = useState({
    paid_date: "", paid_amount: "", payment_method: "Bank Transfer",
    payment_reference: "", notes: "", receipt: null,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !fee) return;
    const today = new Date().toISOString().slice(0, 10);
    setForm({
      paid_date: today,
      paid_amount: String(fee.amount ?? ""),
      payment_method: "Bank Transfer",
      payment_reference: "", notes: "", receipt: null,
    });
  }, [open, fee]);

  const onFile = async (file) => {
    if (!file) { setForm((p) => ({ ...p, receipt: null })); return; }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Receipt must be under 10MB"); return;
    }
    const data_base64 = await fileToBase64(file);
    setForm((p) => ({ ...p, receipt: {
      filename: file.name, content_type: file.type || "application/octet-stream", data_base64,
    }}));
  };

  const submit = async () => {
    if (!form.paid_date) { toast.error("Paid date required"); return; }
    const amt = parseFloat(form.paid_amount);
    if (Number.isNaN(amt) || amt <= 0) { toast.error("Paid amount must be > 0"); return; }
    setBusy(true);
    try {
      await api.post(`/payouts/fees/${fee.id}/pay`, {
        paid_date: form.paid_date,
        paid_amount: amt,
        payment_method: form.payment_method,
        payment_reference: form.payment_reference,
        notes: form.notes,
        receipt: form.receipt,
      });
      toast.success(`${fee.fee_type} marked paid`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open || !fee) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md"
                     data-testid="payouts-pay-dialog">
        <DialogHeader>
          <DialogTitle>Mark as paid — {fee.fee_type}</DialogTitle>
        </DialogHeader>
        <div className="rounded-md border border-slate-800 bg-slate-800/30 p-2 mb-1 text-xs">
          <div className="text-slate-400">{fee.candidate_name}{fee.client_name ? ` · ${fee.client_name}` : ""}</div>
          <div className="num text-slate-300">Due {fmtDate(fee.due_date)} · Billed {fmtMoney(fee.amount)}</div>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-slate-400">Paid date *</Label>
              <Input type="date" value={form.paid_date}
                     onChange={(e) => setForm({ ...form, paid_date: e.target.value })}
                     className="bg-[#0a0e1a] border-slate-800 mt-1"
                     data-testid="payouts-pay-date" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Amount paid (RM) *</Label>
              <Input type="number" step="0.01" value={form.paid_amount}
                     onChange={(e) => setForm({ ...form, paid_amount: e.target.value })}
                     className="bg-[#0a0e1a] border-slate-800 mt-1 num"
                     data-testid="payouts-pay-amount" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Method</Label>
            <select value={form.payment_method}
                    onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                    className="w-full bg-[#0a0e1a] border border-slate-800 rounded h-9 px-2 text-sm mt-1"
                    data-testid="payouts-pay-method">
              {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Reference (txn ID, cheque #)</Label>
            <Input value={form.payment_reference}
                   onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
                   className="bg-[#0a0e1a] border-slate-800 mt-1"
                   placeholder="optional"
                   data-testid="payouts-pay-ref" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Notes</Label>
            <Input value={form.notes}
                   onChange={(e) => setForm({ ...form, notes: e.target.value })}
                   className="bg-[#0a0e1a] border-slate-800 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400 flex items-center gap-1.5">
              <Upload className="w-3 h-3" /> Receipt (optional)
            </Label>
            <input type="file" accept="image/*,application/pdf"
                   onChange={(e) => onFile(e.target.files?.[0])}
                   className="block w-full mt-1 text-xs text-slate-400 file:mr-2 file:rounded file:border-0 file:bg-slate-700 file:text-slate-100 file:px-2 file:py-1 file:text-xs hover:file:bg-slate-600"
                   data-testid="payouts-pay-receipt" />
            {form.receipt && (
              <div className="text-[11px] text-emerald-300 mt-1">✓ {form.receipt.filename}</div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="payouts-pay-submit">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark paid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
